import { useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import { useToolTokens, useElementSize, keepFocus } from './toolStyles';

// A window that floats above the app: moved by its title bar, resized from any edge or corner, brought to the front
// by a press anywhere on it, reset (position and size) by a double-click on the title bar.
//
// It is plain fixed positioning, NOT a MUI Modal: a Modal would take focus, mark the whole app aria-hidden and fight the
// Dialog or Drawer the person may have open underneath (see modalIsOpen in toolStyles.js). Each tool focuses its own
// root when it opens (useFocusOnOpen), so its keyboard handling is on the element that has the focus.

/** Keep a rectangle usable: never smaller than its minimum, never bigger than the screen, and with at least a
 *  stretch of its title bar still on screen so it can always be grabbed. */
export function clampRect(r, vw, vh, minW, minH) {
  const w = Math.min(Math.max(r.w, minW), Math.max(minW, vw));
  const h = Math.min(Math.max(r.h, minH), Math.max(minH, vh));
  const grab = Math.min(120, w);
  return {
    w, h,
    x: Math.min(Math.max(r.x, grab - w), vw - grab),
    y: Math.min(Math.max(r.y, 0), Math.max(0, vh - 44)),
  };
}

const HANDLES = [
  { dir: 'n', cursor: 'ns-resize', sx: { top: -3, left: 12, right: 12, height: 8 } },
  { dir: 's', cursor: 'ns-resize', sx: { bottom: -3, left: 12, right: 12, height: 8 } },
  { dir: 'e', cursor: 'ew-resize', sx: { right: -3, top: 12, bottom: 12, width: 8 } },
  { dir: 'w', cursor: 'ew-resize', sx: { left: -3, top: 12, bottom: 12, width: 8 } },
  { dir: 'nw', cursor: 'nwse-resize', sx: { top: -3, left: -3, width: 16, height: 16 } },
  { dir: 'ne', cursor: 'nesw-resize', sx: { top: -3, right: -3, width: 16, height: 16 } },
  { dir: 'sw', cursor: 'nesw-resize', sx: { bottom: -3, left: -3, width: 16, height: 16 } },
  { dir: 'se', cursor: 'nwse-resize', sx: { bottom: -3, right: -3, width: 16, height: 16 } },
];

/**
 * @param rect      { x, y, w, h } in CSS px - owned by the caller (so it can be remembered)
 * @param onRect    called with the new rect while moving / resizing
 * @param onCommit  called once when a move / resize ends (the moment to store it)
 * @param onReset   double-click on the title bar
 * @param children  a function of the body's measured size: ({ w, h }) => node
 */
export default function FloatingWindow({
  name, title, icon, shortcut, rect, minW = 280, minH = 320, zIndex, escapeCloses = true,
  onRect, onCommit, onReset, onRaise, onClose, children,
}) {
  const T = useToolTokens();
  const { t } = useTranslation();
  const rootRef = useRef(null);
  const bodyRef = useRef(null);
  const gesture = useRef(null);
  const size = useElementSize(bodyRef);

  const begin = (e, mode) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();                                   // no text selection while dragging
    onRaise();
    e.currentTarget.setPointerCapture(e.pointerId);
    gesture.current = { mode, sx: e.clientX, sy: e.clientY, r: rect };
  };
  const step = (e) => {
    const g = gesture.current;
    if (!g) return;
    const dx = e.clientX - g.sx;
    const dy = e.clientY - g.sy;
    let { x, y, w, h } = g.r;
    if (g.mode === 'move') {
      x += dx; y += dy;
    } else {
      if (g.mode.includes('e')) w += dx;
      if (g.mode.includes('s')) h += dy;
      if (g.mode.includes('w')) { w -= dx; x += dx; }
      if (g.mode.includes('n')) { h -= dy; y += dy; }
      // at the minimum the opposite edge stays put instead of the window being pushed along
      if (w < minW) { if (g.mode.includes('w')) x -= minW - w; w = minW; }
      if (h < minH) { if (g.mode.includes('n')) y -= minH - h; h = minH; }
    }
    onRect(clampRect({ x, y, w, h }, window.innerWidth, window.innerHeight, minW, minH));
  };
  const end = (e) => {
    if (!gesture.current) return;
    gesture.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* already released */ }
    onCommit();
  };
  const dragProps = (mode) => ({
    onPointerDown: (e) => begin(e, mode), onPointerMove: step, onPointerUp: end, onPointerCancel: end,
  });

  return (
    <Box
      ref={rootRef}
      role="dialog"
      aria-modal="false"
      aria-label={title}
      tabIndex={-1}
      data-xms-tool={name}
      onPointerDown={onRaise}
      onKeyDown={(e) => { if (escapeCloses && e.key === 'Escape') { e.stopPropagation(); onClose(); } }}
      sx={{
        position: 'fixed', left: rect.x, top: rect.y, width: rect.w, height: rect.h, zIndex,
        display: 'flex', flexDirection: 'column', overflow: 'visible',
        bgcolor: T.BG, color: T.TEXT, border: `1px solid ${T.BD2}`, borderRadius: '14px', boxShadow: T.SHADOW,
        outline: 'none',
      }}
    >
      {/* title bar: drag to move, double-click to reset */}
      <Box
        {...dragProps('move')}
        onDoubleClick={onReset}
        title={t('tools.dragHint')}
        data-tool-titlebar
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, height: 40, px: 1.5, flexShrink: 0,
          bgcolor: T.HEAD, borderBottom: `1px solid ${T.BD}`, borderRadius: '13px 13px 0 0',
          cursor: 'grab', userSelect: 'none', touchAction: 'none',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <Box sx={{ display: 'flex', color: T.TEXT2, '& svg': { fontSize: 18 } }}>{icon}</Box>
        <Typography noWrap sx={{ flexGrow: 1, fontSize: '0.82rem', fontWeight: 700, color: T.TEXT }}>{title}</Typography>
        {shortcut && (
          // a span, not <kbd>: App.css still carries Bootstrap's kbd rule (white on a solid dark fill)
          <Box component="span" data-tool-shortcut sx={{
            fontFamily: 'inherit', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.04em', color: T.TEXT2,
            border: `1px solid ${T.BD2}`, borderRadius: '5px', px: 0.6, lineHeight: '16px',
          }}>{shortcut}</Box>
        )}
        <Tooltip title={t('common.close')} placement="bottom">
          <IconButton
            size="small" aria-label={t('common.close')} data-tool-close
            onPointerDown={(e) => e.stopPropagation()}        // a press on the button must not start a drag
            onMouseDown={keepFocus}
            onClick={onClose}
            sx={{ color: T.TEXT2, width: 26, height: 26, '&:hover': { color: T.TEXT, bgcolor: T.CTRL_HOVER } }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* the tool itself, told how big the room is */}
      <Box ref={bodyRef} sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden', position: 'relative', borderRadius: '0 0 13px 13px' }}>
        {size.w > 0 && children(size)}
      </Box>

      {HANDLES.map((h) => (
        <Box key={h.dir} data-resize={h.dir} {...dragProps(h.dir)}
          sx={{ position: 'absolute', zIndex: 2, cursor: h.cursor, touchAction: 'none', ...h.sx }} />
      ))}
    </Box>
  );
}
